import React, { useState } from 'react';
import { X, Calendar, Clock, DollarSign, Briefcase } from 'lucide-react';

const CreateJobForm = ({ onSubmit, onCancel, loading, employerData }) => {
    const chargePH = employerData?.chargePH || 125;

    const [formData, setFormData] = useState({
        title: '',
        description: '',
        date: '',
        time: '',
        duration: '',
        languages: '', // Comma separated string for input
        vacancies: 1,
        budget: {
            value: '',
        }
    });

    const [error, setError] = useState('');

    // Get tomorrow's date for min attribute
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const minDate = tomorrow.toISOString().split('T')[0];

    const handleChange = (e) => {
        const { name, value } = e.target;
        setError(''); // Clear error on change

        // Special handling for duration to auto-calc budget
        if (name === 'duration') {
            const numDuration = parseFloat(value);
            const calculatedBudget = numDuration && !isNaN(numDuration)
                ? Math.max(500, numDuration * chargePH)
                : 500;

            setFormData(prev => ({
                ...prev,
                duration: value,
                budget: {
                    ...prev.budget,
                    value: calculatedBudget
                }
            }));
            return;
        }

        if (name.includes('.')) {
            const [parent, child] = name.split('.');
            setFormData(prev => ({
                ...prev,
                [parent]: {
                    ...prev[parent],
                    [child]: value
                }
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        // Validate date
        if (formData.date < minDate) {
            setError('Date must be tomorrow or later');
            return;
        }

        // Process form data
        // Process form data
        const isoDate = new Date(`${formData.date}T${formData.time}:00`).toISOString();

        const processedData = {
            ...formData,
            date: isoDate,
            vacancies: Number(formData.vacancies),
            languages: formData.languages.split(',').map(lang => lang.trim()).filter(Boolean),
            duration: Number(formData.duration),
            budget: {
                value: Number(formData.budget.value),
                currency: 'INR'
            },
            status: 'pending', // Initial status
            detailedStatus: 'created_by_employer',
        };

        onSubmit(processedData);
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                <div className="p-6 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
                    <h2 className="text-2xl font-light text-gray-800">Post a New Job</h2>
                    <button onClick={onCancel} className="text-gray-500 hover:text-gray-700">
                        <X size={24} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-6">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-md text-sm">
                            {error}
                        </div>
                    )}

                    {/* Basic Info */}
                    <div className="space-y-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                            <div className="relative">
                                <Briefcase className="absolute left-3 top-3 text-gray-400" size={18} />
                                <input
                                    type="text"
                                    name="title"
                                    required
                                    value={formData.title}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                                    placeholder="e.g. Professional Plumber Needed"
                                />
                            </div>
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Description / Requirements</label>
                            <textarea
                                name="description"
                                required
                                value={formData.description}
                                onChange={handleChange}
                                rows="4"
                                className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                                placeholder="Describe the job details and any specific requirements..."
                            />
                        </div>
                    </div>

                    {/* Schedule */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                            <div className="relative">
                                <Calendar className="absolute left-3 top-3 text-gray-400" size={18} />
                                <input
                                    type="date"
                                    name="date"
                                    required
                                    min={minDate}
                                    value={formData.date}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                            <div className="relative">
                                <Clock className="absolute left-3 top-3 text-gray-400" size={18} />
                                <input
                                    type="time"
                                    name="time"
                                    required
                                    value={formData.time}
                                    onChange={handleChange}
                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                                />
                            </div>
                        </div>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Duration (Hours)</label>
                            <input
                                type="number"
                                name="duration"
                                min="4"
                                step="0.5"
                                required
                                value={formData.duration}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                                placeholder="minimum 4 hours"
                            />
                        </div>
                    </div>

                    {/* Budget */}
                    <div className="space-y-4">
                        <h3 className="text-lg font-medium text-gray-800">Budget per person (INR)</h3>
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Amount (₹)</label>
                            <div className="relative">
                                <span className="absolute left-3 top-2 text-gray-500">₹</span>
                                <input
                                    type="number"
                                    name="budget.value"
                                    readOnly
                                    value={formData.budget.value}
                                    className="w-full pl-8 pr-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-gray-500 cursor-not-allowed"
                                />
                                <Briefcase className="absolute right-3 top-2 text-gray-400" size={20} />
                            </div>
                            <p className="text-xs text-gray-500 mt-1">Calculated based on duration (Min ₹500)</p>
                        </div>
                    </div>

                    {/* Cool-off Notice */}
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-0.5">
                            <Clock className="w-5 h-5 text-blue-500" />
                        </div>
                        <div>
                            <h4 className="text-sm font-medium text-blue-800">Cool-off Period</h4>
                            <p className="text-sm text-blue-700 mt-1">
                                There will be a 30 mins cool-off time where you will not be charged. The professional will reach 30 mins prior to the job start time for training.
                            </p>
                        </div>
                    </div>

                    {/* Additional Info */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Languages (comma separated)</label>
                            <input
                                type="text"
                                name="languages"
                                value={formData.languages}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                                placeholder="English, Hindi, etc."
                            />
                        </div>

                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">Vacancies (1-100)</label>
                            <input
                                type="number"
                                name="vacancies"
                                min="1"
                                max="100"
                                required
                                value={formData.vacancies}
                                onChange={handleChange}
                                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-black focus:border-transparent"
                                placeholder="1-100"
                            />
                        </div>
                    </div>

                    <div className="pt-4 flex justify-end space-x-4">
                        <button
                            type="button"
                            onClick={onCancel}
                            className="px-6 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            className="px-6 py-2 bg-black text-white rounded-md hover:bg-gray-800 disabled:opacity-50"
                            disabled={loading}
                        >
                            {loading ? 'Posting...' : 'Post Job'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default CreateJobForm;
